// Reportes de incidentes

import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useUser } from '../UserContext';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatMX } from '../util/DateUtil';
import { apiFetch, apiFetchJson } from '../fetchWrapper';
import ReportForm from './ReportForm';
import GButton from './GButton';
import { getReportStatusLabel } from './ReportStatus';


export default function ReportIncidents({ onSelectReport }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reports, setReports] = useState([]);
  const [sites, setSites] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showReportForm, setShowReportForm] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const { user, setUser, hasRole } = useUser();

  const statusOrder = {
    NEW: 1,
    CONFIRMED: 2,
    IN_PROGRESS: 3,
    RESOLVED: 4,
    CLOSED: 5,
  };

  // Fetch all initial data
  useEffect(() => {
    apiFetchJson("/api/reports").then(setReports);
    apiFetchJson("/api/employees").then(setEmployees);
    apiFetchJson("/api/sites").then(setSites);
  }, []);

  const handleAdd = () => {
    setEditingReport(null);
    setShowReportForm(true);
  };

  const handleEdit = (report) => {
    setEditingReport(report);
    setShowReportForm(true);
  };


  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este reporte?')) {
      return;
    }
    try {
      const res = await apiFetch(`/api/reports/${id}`, {
        method: 'DELETE',
      });
      if (!res) return;
      if (!res.ok) {
        throw new Error('Error al eliminar el reporte');
      }
      setReports(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      console.error(err);
      alert(err.message || 'Error al eliminar el reporte');
    }
  };


  const handleExport = async () => {
    const doc = new jsPDF();

    // Optional: Add title
    doc.setFontSize(18);
    doc.text("Reportes", 14, 22);

    // Prepare table data
    const tableColumn = ["ID", "Ubicación", "Asunto", "Descripción", "Estado", "Encargado", "Autor", "Actualizado"];
    const tableRows = reports.map((r) => [
      r.id,
      getSiteName(r.siteId),
      r.subject || '-',
      r.description || '-',
      getReportStatusLabel(r.reportStatus),
      getEmployeeName(r.assignedToId),
      getEmployeeName(r.authorId),
      formatMX(r.createdAt),
      formatMX(r.updatedAt),
    ]);

    // Add autoTable
    autoTable(doc, {
      startY: 30, // start below title
      head: [tableColumn],
      body: tableRows,
      theme: "striped",
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      styles: { fontSize: 10 },
    });

    // Save the PDF
    doc.save("reportes.pdf");
  };

  const handleCancelForm = () => {
    setShowReportForm(false);
    setEditingReport(null);
  };

  const handleSaveReport = (savedReport) => {
    setReports((prevReports) => {
      const exists = prevReports.some(r => r.id === savedReport.id);
      if (exists) {
        return prevReports.map(r =>
          r.id === savedReport.id ? savedReport : r
        );
      } else {
        return [...prevReports, savedReport];
      }
    });
    setShowReportForm(false);
    setEditingReport(null);
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site ? site.client.companyName + " - " + site.name : "Desconocida";
  }

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    return employee ? `${employee.user.person.firstname} ${employee.user.person.lastnameFirst}` : "";
  }

  const filteredReports = reports.filter(report => {
    const fullSearchTerm = `${getSiteName(report.siteId)} ${report.subject} ${report.description} ${getEmployeeName(report.assignedToId)} ${getEmployeeName(report.authorId)}`.toLowerCase();
    if (hasRole(['EMPLOYEE']) && (report.authorId !== user.id && report.assignedToId !== user.id)) {
      return false;
    }
    return fullSearchTerm.includes(search.toLowerCase());
  });

  const handleSelectReport = (report) => {
    onSelectReport(report);
  };

  const sortedReports = [...filteredReports].sort((a, b) => {
    let valA;
    let valB;

    switch (orderBy) {
      case 'status': // estado
        valA = statusOrder[a.status];
        valB = statusOrder[b.status];
        break;
      case 'createdAt':
        valA = new Date(a.createdAt);
        valB = new Date(b.createdAt);
        break;
      case 'updatedAt':
        valA = new Date(a.updatedAt);
        valB = new Date(b.updatedAt);
        break;
      case 'author':
        valA = a.contactPerson.firstname;
        valB = b.contactPerson.firstname;
        break;
      case 'id':
        valA = a.id;
        valB = b.id;
        break;
      default:
        valA = a[orderBy];
        valB = b[orderBy];
    }

    if (valA < valB) return order === 'asc' ? -1 : 1;
    if (valA > valB) return order === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  return (
    <>

      {showReportForm && (
        <>
          <ReportForm report={editingReport} onCancel={handleCancelForm} onSave={handleSaveReport} />
          <br />
        </>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>

        <Box sx={{
          display: "flex",
          gap: 1,
          mr: 2,
          flexDirection: { xs: "column", sm: "row" },
          width: { xs: "100%", sm: "auto" },
        }}>
          <Button sx={{
            backgroundColor: "#3f74b1",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#364f6b"
            }
          }}
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}>
            Crear Reporte
          </Button>
          <Button

            variant="outlined"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleExport}>
            Exportar
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{
        p: 1, overflowX: 'auto', maxHeight: '100%'
      }}>
        <Box sx={{ width: '100%', px: 1, py: 0.5 }}>
          <Typography variant="h6">Lista de incidentes</Typography>
        </Box>

        <TextField
          label="Buscar"
          variant="outlined"
          size="small"
          fullWidth
          sx={{ mb: 2 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <Table>
          <TableHead
            sx={{
              backgroundColor: '#f5f5f5',
              textTransform: 'uppercase',
            }}>
            <TableRow>
              {/* <TableCell sx={{ width: 60, fontWeight: "bold" }}>ID</TableCell> */}
              <TableCell sx={{ width: 60, fontWeight: "bold" }}>
                <TableSortLabel
                  active={orderBy === 'id'}
                  direction={orderBy === 'id' ? order : 'asc'}
                  onClick={() => handleSort('id')}>
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: 150, fontWeight: "bold" }}>Ubicación</TableCell>
              <TableCell sx={{ width: 150, fontWeight: "bold" }}>Asunto</TableCell>
              <TableCell sx={{ width: 150, fontWeight: "bold", display: { xs: "none", sm: "table-cell" } }}>Descripción</TableCell>
              <TableCell sx={{ width: 100, fontWeight: "bold", display: { xs: "none", sm: "table-cell" } }}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleSort('status')}>
                  Estatus
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: 100, fontWeight: "bold" }}>Autor</TableCell>
              <TableCell sx={{ width: 100, fontWeight: "bold", display: { xs: "none", sm: "table-cell" } }}>Encargado</TableCell>
              <TableCell sx={{ width: 100, fontWeight: "bold", display: { xs: "none", sm: "table-cell" } }}>
                <TableSortLabel
                  active={orderBy === 'createdAt'}
                  direction={orderBy === 'createdAt' ? order : 'asc'}
                  onClick={() => handleSort('createdAt')}>
                  Creado
                </TableSortLabel>
              </TableCell>

              <TableCell sx={{ width: 100, fontWeight: "bold", display: { xs: "none", sm: "table-cell" } }}>
                <TableSortLabel
                  active={orderBy === 'updatedAt'}
                  direction={orderBy === 'updatedAt' ? order : 'asc'}
                  onClick={() => handleSort('updatedAt')}>
                  Actualizado
                </TableSortLabel>
              </TableCell>

              <TableCell sx={{ width: 100, fontWeight: "bold" }}>Acciones</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedReports.map((report) => (
              <TableRow key={report.id}
                hover
                sx={{
                  "&:hover .MuiTableCell-root": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <TableCell>{report.id}</TableCell>
                <TableCell>{getSiteName(report.siteId)}</TableCell>
                <TableCell>{report.subject}</TableCell>

                <TableCell
                  sx={{ display: { xs: "none", sm: "table-cell" } }}
                >
                  {report.description}
                </TableCell>
                <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>{getReportStatusLabel(report.reportStatus)}</TableCell>
                <TableCell>{getEmployeeName(report.authorId)}</TableCell>
                <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>{getEmployeeName(report.assignedToId)}</TableCell>
                <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>{formatMX(new Date(report.createdAt))}</TableCell>
                <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>{formatMX(new Date(report.updatedAt))}</TableCell>
                <TableCell
                  sx={{ whiteSpace: "nowrap" }} >
                  <GButton
                    variant="clearBlue"
                    onClick={() => handleSelectReport(report)}
                  >Ver detalle
                  </GButton>
                  <Tooltip title="Editar"
                    slotProps={{
                      tooltip: {
                        sx: {
                          fontSize: '12px'
                        }
                      }
                    }}
                  >
                    <IconButton
                      color="primary"
                      onClick={() => handleEdit(report)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Borrar"
                    slotProps={{
                      tooltip: {
                        sx: {
                          fontSize: '12px'
                        }
                      }
                    }}
                  >
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(report.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}